import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { ZIndex } from '@rover/constants/src/styles/ZIndex';
import { Box, Flex } from '@rover/kibble/core';
import useReducedMotion from '@rover/kibble/hooks/useReducedMotion';

import announceToast from './liveAnnouncer';
import Toast from './Toast';
import {
  MAX_VISIBLE_TOASTS,
  SLOT_SHIFT_DURATION,
  TOAST_EDGE_OFFSET,
  TOAST_MAX_WIDTH,
  TOAST_STACK_GAP,
  ToastHandle,
} from './Toast.common';
import { getEvictionCandidates, getVisibleToasts } from './toastListReducer';
import {
  DEFAULT_STACK_PLACEMENT,
  getNeighborIds,
  getNextFocusTarget,
  TOAST_STACK_TEST_ID,
  ToastStackProps,
} from './ToastStack.common';
import useDoubleStackWarning from './useDoubleStackWarning';
import useToastAnnouncements from './useToastAnnouncements';

/** The stack renders nothing on the server, where `useLayoutEffect` only warns. */
const useLayoutEffectOnClient = typeof window === 'undefined' ? useEffect : useLayoutEffect;

/**
 * The toast region for a screen. Owns what belongs to the list rather than to one toast: the
 * portal, the gap, the cap, the shared pause, each arrival's announcement, and focus recovery.
 *
 * Portalled to `document.body` so no ancestor can trap it, while staying in the host's React tree.
 */
const ToastStack = ({
  toasts,
  onDismiss,
  onExitStart,
  placement = DEFAULT_STACK_PLACEMENT,
  maxVisible = MAX_VISIBLE_TOASTS,
  testID = TOAST_STACK_TEST_ID,
}: ToastStackProps): JSX.Element | null => {
  useDoubleStackWarning(toasts);
  const isReducedMotion = useReducedMotion();
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isFocusWithin, setIsFocusWithin] = useState(false);

  const containerRef = useRef<HTMLElement>(null);
  // The dismiss handle, the wrapper element, and Banner's ✕ — to move focus onto it.
  const handles = useRef(new Map<string, ToastHandle | null>()).current;
  const wrappers = useRef(new Map<string, HTMLElement | null>()).current;
  const closeButtons = useRef(new Map<string, HTMLElement | null>()).current;
  const previouslyFocused = useRef<Element | null>(null);
  // id → the top the toast was last painted at: where a slot change is played back from.
  const paintedTops = useRef(new Map<string, number>()).current;
  // Taken out of the column for their exit, so the survivors close the gap once, as it starts.
  const pinned = useRef(new Set<string>()).current;
  const slideFrame = useRef<number | undefined>(undefined);
  const previousCount = useRef(0);
  const toastsRef = useRef(toasts);
  toastsRef.current = toasts;

  useEffect(() => {
    setPortalTarget(document.body);
  }, []);

  // Where focus was before the first toast appeared, to hand it back when the last one leaves.
  useEffect(() => {
    if (previousCount.current === 0 && toasts.length > 0) {
      previouslyFocused.current = document.activeElement;
    }
    previousCount.current = toasts.length;
  }, [toasts.length]);

  // Over the cap: the oldest leave fast, through their own `onClose`. Before paint, so the arrival
  // and the eviction it caused land in the same frame and the column is never drawn over the cap.
  useLayoutEffectOnClient(() => {
    getEvictionCandidates(toasts, maxVisible).forEach((toast) => {
      handles.get(toast.id)?.dismiss({ accelerated: true });
    });
  }, [toasts, maxVisible, handles]);

  // A flex column relays a slot change as a jump in one frame, so it is played back as a slide:
  // measured before paint, offset to where the toast was last seen, then released to travel.
  const playSlotShift = useCallback((): void => {
    const measured = new Map<string, number>();
    const sliding: HTMLElement[] = [];

    toastsRef.current.forEach((toast) => {
      // A pinned toast holds a position of its own and is not the column's to move.
      if (pinned.has(toast.id)) return;

      const element = wrappers.get(toast.id);
      if (!element) return;

      // Cleared before measuring, so a shift in flight is not read back. `none`, not '': dropping
      // the declaration leaves the transition running and the offset is measured mid-flight.
      Object.assign(element.style, { transition: 'none', transform: 'none' });

      const { top } = element.getBoundingClientRect();
      measured.set(toast.id, top);

      // Nothing to play back for a toast not yet painted: its own entry brings it in.
      const previous = paintedTops.get(toast.id);
      if (isReducedMotion || previous === undefined || previous === top) return;

      element.style.transform = `translateY(${previous - top}px)`;
      sliding.push(element);
    });

    if (slideFrame.current !== undefined) cancelAnimationFrame(slideFrame.current);
    slideFrame.current = requestAnimationFrame(() => {
      slideFrame.current = undefined;
      measured.forEach((top, id) => paintedTops.set(id, top));
      sliding.forEach((element) => {
        Object.assign(element.style, {
          transition: `transform ${SLOT_SHIFT_DURATION}ms ease`,
          transform: 'none',
        });
      });
    });
  }, [wrappers, paintedTops, pinned, isReducedMotion]);

  useLayoutEffectOnClient(playSlotShift, [toasts, playSlotShift]);

  useEffect(
    () => () => {
      if (slideFrame.current !== undefined) cancelAnimationFrame(slideFrame.current);
    },
    []
  );

  // `dismissToast(id)` asks through the record rather than reaching for a handle it cannot see.
  useEffect(() => {
    toasts.forEach((toast) => {
      if (toast.isDismissing) handles.get(toast.id)?.dismiss();
    });
  }, [toasts, handles]);

  // The live region already serializes a burst, so no stagger is needed here.
  useToastAnnouncements(toasts, maxVisible, announceToast);

  // Written straight to the element rather than through state, so the removal lands in the same
  // commit as whatever prompted it — a ✕ press changes no state at all, and still closes the gap.
  const handleExitStart = useCallback(
    (id: string): void => {
      onExitStart?.(id);

      const element = wrappers.get(id);
      if (!element || pinned.has(id)) return;

      // Left where it appears, not where the column would put it: mid-eviction the column holds a
      // slot more than it is ever drawn with. So the slot last drawn in, plus any shift left to run.
      const drawn = element.getBoundingClientRect();
      Object.assign(element.style, { transition: 'none', transform: 'none' });
      const laidOut = element.getBoundingClientRect();
      const top = (paintedTops.get(id) ?? laidOut.top) + (drawn.top - laidOut.top);

      // Portalled to `body` with no containing block above, so a viewport position is the answer.
      Object.assign(element.style, {
        position: 'fixed',
        top: `${top}px`,
        left: `${laidOut.left}px`,
        width: `${laidOut.width}px`,
      });

      pinned.add(id);
      paintedTops.delete(id);
      playSlotShift();
    },
    [wrappers, pinned, paintedTops, playSlotShift, onExitStart]
  );

  const handleToastClose = useCallback(
    (id: string): void => {
      const wrapper = wrappers.get(id);
      if (wrapper?.contains(document.activeElement)) {
        const visibleIds = getVisibleToasts(toastsRef.current, maxVisible).map((toast) => toast.id);
        const { newer, older } = getNeighborIds(visibleIds, id);
        getNextFocusTarget({
          newerNeighbor: newer === undefined ? undefined : closeButtons.get(newer),
          olderNeighbor: older === undefined ? undefined : closeButtons.get(older),
          previouslyFocused: previouslyFocused.current,
          container: containerRef.current,
        })?.focus();
      }

      handles.delete(id);
      wrappers.delete(id);
      closeButtons.delete(id);
      pinned.delete(id);
      paintedTops.delete(id);
      onDismiss(id);
    },
    [onDismiss, maxVisible, handles, wrappers, closeButtons, pinned, paintedTops]
  );

  const handleBlur = (event: React.FocusEvent<HTMLElement>): void => {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
      setIsFocusWithin(false);
    }
  };

  if (!portalTarget) return null;

  return createPortal(
    // Mounted even when empty: the last resort for focus when the final toast leaves.
    <Box
      ref={containerRef}
      position="fixed"
      left={TOAST_EDGE_OFFSET}
      right={TOAST_EDGE_OFFSET}
      {...(placement === 'top' ? { top: TOAST_EDGE_OFFSET } : { bottom: TOAST_EDGE_OFFSET })}
      maxWidth={TOAST_MAX_WIDTH}
      zIndex={ZIndex.TOAST.toString()}
      testID={testID}
      webProps={{
        tabIndex: -1,
        onMouseEnter: () => setIsHovered(true),
        onMouseLeave: () => setIsHovered(false),
        onFocus: () => setIsFocusWithin(true),
        onBlur: handleBlur,
      }}
      sx={{ mx: 'auto', outline: 'none' }}
    >
      {/* Oldest first in the array; for `top`, reversing puts the newest nearest that edge. */}
      <Flex flexDirection={placement === 'top' ? 'column-reverse' : 'column'} gap={TOAST_STACK_GAP}>
        {toasts.map((toast) => (
          <Box
            key={toast.id}
            ref={(element: HTMLElement | null) => {
              wrappers.set(toast.id, element);
            }}
          >
            <Toast
              ref={(handle: ToastHandle | null) => {
                handles.set(toast.id, handle);
              }}
              webProps={{
                closeButtonRef: (element: HTMLElement | null) => {
                  closeButtons.set(toast.id, element);
                },
              }}
              isStacked
              placement={placement}
              announce={false}
              isPaused={isHovered || isFocusWithin}
              timerEpoch={toast.timerEpoch}
              autoHideDuration={toast.autoHideDuration}
              severity={toast.severity}
              title={toast.title}
              link={toast.link}
              onExitStart={() => handleExitStart(toast.id)}
              onClose={() => handleToastClose(toast.id)}
            >
              {toast.message}
            </Toast>
          </Box>
        ))}
      </Flex>
    </Box>,
    portalTarget
  );
};

export default ToastStack;
